import React, { useState } from 'react';
import { buildVideoPassport } from '../validation/videoPassport';
import { videoCoherence } from '../validation/videoCoherence';
import { videoPodRealization } from '../validation/videoPodRealization';

const STATE = {
  pass: { text: 'var(--success-text)', bg: 'var(--success-bg)', border: 'var(--success-border)' },
  warn: { text: 'var(--warn-text)', bg: 'var(--warn-bg)', border: 'var(--warn-border)' },
  fail: { text: 'var(--error-text)', bg: 'var(--error-bg)', border: 'var(--error-border)' },
  info: { text: 'var(--neutral-text)', bg: 'var(--neutral-bg)', border: 'var(--neutral-border)' },
};

function StateMarker({ state }) {
  const s = STATE[state] || STATE.info;
  if (state === 'warn') {
    return <span style={{ color: s.text, fontWeight: 700, fontSize: 11 }}>▲</span>;
  }
  if (state === 'fail') {
    return <span style={{ color: s.text, fontWeight: 700, fontSize: 11 }}>✕</span>;
  }
  return <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.text, flexShrink: 0 }} />;
}

function PassportRow({ row }) {
  const s = STATE[row.state] || STATE.info;
  const filled = row.state === 'warn' || row.state === 'fail';
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        padding: '5px 0',
        borderBottom: '1px solid var(--line)',
        alignItems: 'flex-start',
        borderLeft: filled ? `3px solid ${s.border}` : '3px solid transparent',
        paddingLeft: 8,
        background: filled ? s.bg : undefined,
      }}
    >
      <span style={{ width: 92, flexShrink: 0, fontSize: 11, color: 'var(--muted)' }}>{row.label}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <StateMarker state={row.state} />
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>{row.value}</span>
        </div>
        {row.note && (
          <div style={{ fontSize: 10.5, color: 'var(--muted)', fontFamily: 'var(--font-mono)', marginTop: 2, wordBreak: 'break-word' }}>
            {row.note}
          </div>
        )}
      </div>
    </div>
  );
}

// One request↔VAST coherence check (after the response comes back).
function CoherenceCheck({ c }) {
  const s = STATE[c.state] || STATE.info;
  const filled = c.state === 'warn' || c.state === 'fail';
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        padding: '5px 0',
        borderBottom: '1px solid var(--line)',
        alignItems: 'flex-start',
        borderLeft: filled ? `3px solid ${s.border}` : '3px solid transparent',
        paddingLeft: 8,
        background: filled ? s.bg : undefined,
      }}
    >
      <StateMarker state={c.state} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>{c.label}</div>
        <div style={{ fontSize: 10.5, color: 'var(--muted)', fontFamily: 'var(--font-mono)', marginTop: 2, wordBreak: 'break-word' }}>
          {c.requested} → {c.delivered}
        </div>
        {c.note && (
          <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 2, wordBreak: 'break-word' }}>{c.note}</div>
        )}
      </div>
    </div>
  );
}

// Small uppercase section label inside the disclosure.
function SubHead({ children }) {
  return (
    <div
      style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '.08em',
        textTransform: 'uppercase',
        color: 'var(--muted)',
        margin: '10px 0 2px',
      }}
    >
      {children}
    </div>
  );
}

// Collapsed-by-default disclosure for the "after VAST" checks — request↔creative
// coherence and pod realization. The badge shows the combined flag count at rest
// so the reviewer knows whether to expand before doing so. Either section may be
// absent (coherence null until a VAST is attached; pod only for VMAP/pod video).
function ChecksDisclosure({ coh, pod }) {
  const [open, setOpen] = useState(false);
  const flagged = (coh ? coh.verdict.flagged : 0) + (pod ? pod.verdict.flagged : 0);
  const countColors = flagged > 0 ? STATE.fail : STATE.pass;
  const hasCoh = coh && coh.checks.length > 0;
  const hasPod = pod && pod.checks.length > 0;
  const title = hasPod ? 'Coherence & pod checks' : 'Coherence checks';
  return (
    <div style={{ marginTop: 10 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'var(--bg-raised)',
          border: '1px solid var(--line)',
          borderRadius: 8,
          padding: '8px 10px',
          cursor: 'pointer',
          color: 'var(--ink)',
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 10, color: 'var(--muted)' }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontSize: 12, fontWeight: 600 }}>{title}</span>
        <span
          style={{
            fontSize: 9,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            color: 'var(--muted)',
            border: '1px solid var(--line)',
            borderRadius: 5,
            padding: '1px 6px',
          }}
        >
          After VAST
        </span>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 10.5,
            fontWeight: 700,
            padding: '2px 9px',
            borderRadius: 20,
            color: countColors.text,
            background: countColors.bg,
          }}
        >
          {flagged > 0 ? `${flagged} flagged` : 'all pass'}
        </span>
      </button>
      {open && (
        <div style={{ marginTop: 4 }}>
          {hasCoh && (
            <>
              <SubHead>Request ↔ creative</SubHead>
              {coh.checks.map((c) => (
                <CoherenceCheck key={c.key} c={c} />
              ))}
            </>
          )}
          {hasPod && (
            <>
              <SubHead>Pod realization</SubHead>
              {pod.checks.map((c) => (
                <CoherenceCheck key={c.key} c={c} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function VideoPassport({ row }) {
  const pass = buildVideoPassport(row);
  if (!pass) return null;

  // Coherence is null until a VAST response is attached; pod realization is null
  // unless this is a VMAP/pod request. The disclosure renders if either exists.
  const coh = videoCoherence(row);
  const pod = videoPodRealization(row);
  const verdictColors = STATE[pass.verdict.level] || STATE.info;

  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid var(--line)',
          padding: '4px 0',
        }}
      >
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--muted)' }}>
            Video Implementation
          </div>
          {pass.iu && (
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>{pass.iu}</div>
          )}
        </div>
        <span
          style={{
            borderRadius: 20,
            padding: '3px 10px',
            fontSize: 11,
            fontWeight: 700,
            color: verdictColors.text,
            background: verdictColors.bg,
          }}
        >
          {pass.verdict.label}
        </span>
      </div>

      <div
        style={{
          fontSize: 9,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: 'var(--muted)',
          border: '1px solid var(--line)',
          borderRadius: 5,
          padding: '2px 7px',
          display: 'inline-block',
          margin: '6px 0',
        }}
      >
        Known at request time
      </div>

      {pass.rows.map((r) => (
        <PassportRow key={r.key} row={r} />
      ))}

      {(coh || pod) && <ChecksDisclosure coh={coh} pod={pod} />}
    </div>
  );
}
